// @ts-strict-ignore
import {ResourceType} from 'common/report/constants';
import {isScdPropertyName} from 'common/report/util/properties';
import type {CustomEventAlternative, CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {Segmentation} from 'common/types/reports/query';
import {isSegmentationFilter, Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';

export function hasUserFilters(customEvent: CustomEventDefinition, customEvents: CustomEventDefinition[]): boolean {
  if (!customEvent || !customEvent.alternatives || customEvent.is_collect_everything_event) {
    // Guard against empty customEvent reference, regular events and Autotrack events (which never have user filters)
    return false;
  }

  const alternatives = customEvent.alternatives;

  const hasUserFiltersOnThisEvent = alternatives.some((alternative) => {
    return alternative.valid_segfilter?.filters.some((f: Segfilter | Segmentation) => {
      if (isSegmentationFilter(f)) {
        return f.resourceType === ResourceType.User && !f.dataGroupId;
      }
      return f.property.source === `user`;
    });
  });

  if (hasUserFiltersOnThisEvent) {
    return true;
  }

  return alternatives.some((alternative) => {
    const subEventMatch = alternative.event.match(/\$custom_event:(.+)/);
    if (subEventMatch) {
      const subEventId = subEventMatch[1];
      if (!customEvents) {
        return false;
      }
      const subEvent = customEvents.find((event) => subEventId === `${event.id}`);
      return hasUserFilters(subEvent, customEvents);
    }

    return false;
  });
}

export function hasSCDFiltersFromOtherDataGroup({
  customEvent,
  customEvents,
  dataGroupId,
}: {
  customEvent: CustomEventDefinition;
  customEvents: CustomEventDefinition[];
  dataGroupId: string;
}): boolean {
  if (!customEvent || !customEvent.alternatives || customEvent.is_collect_everything_event) {
    // Guard against empty customEvent reference, regular events and Autotrack events (which never have user filters)
    return false;
  }

  const alternatives = customEvent.alternatives;

  const hasSCDFiltersFromOtherGroupOnThisEvent = alternatives.some((alternative) => {
    return alternative.valid_segfilter?.filters.some((f: Segfilter | Segmentation) => {
      if (isSegmentationFilter(f)) {
        return f.dataGroupId !== dataGroupId && typeof f.propertyName === `string` && isScdPropertyName(f.propertyName);
      }
      return (
        f.property.data_group_id !== dataGroupId &&
        typeof f.property.name === `string` &&
        isScdPropertyName(f.property.name)
      );
    });
  });

  if (hasSCDFiltersFromOtherGroupOnThisEvent) {
    return true;
  }

  return alternatives.some((alternative) => {
    const subEventMatch = alternative.event.match(/\$custom_event:(.+)/);
    if (subEventMatch) {
      const subEventId = subEventMatch[1];
      if (!customEvents) {
        return false;
      }
      const subEvent = customEvents.find((event) => subEventId === `${event.id}`);
      return hasSCDFiltersFromOtherDataGroup({
        customEvent: subEvent,
        customEvents,
        dataGroupId,
      });
    }

    return false;
  });
}

function doesAlternativesHaveAutotrackEvent(
  eventToCheck: CustomEventDefinition,
  customEvents: CustomEventDefinition[],
) {
  return eventToCheck.alternatives.find((alternativeEvent: CustomEventAlternative) => {
    const eventName = alternativeEvent.event;
    const isCustomEvent = eventName.startsWith(`$custom_event:`);
    const customEventId = isCustomEvent ? Number(eventName.replace(/^\$custom_event:/, ``)) : null;
    if (!isCustomEvent) {
      return false;
    }
    const customEvent = customEvents.find((ev: CustomEventDefinition) => ev.id === customEventId);
    // if it can't find the event as autotrack or custom event, might be hidden. ignore for now.
    if (!customEvent) {
      return false;
    } else if (customEvent.isCollectEverything || customEvent.is_collect_everything_event) {
      return true;
    }
    return doesAlternativesHaveAutotrackEvent(customEvent, customEvents);
  });
}

export function isAutoTrackCustomEvent(customEvent: CustomEventDefinition, customEvents: CustomEventDefinition[]) {
  if (customEvent.isCollectEverything || customEvent.is_collect_everything_event) {
    return true;
  }
  return doesAlternativesHaveAutotrackEvent(customEvent, customEvents);
}
